/**
 * apps/battle/client/src/terminal/screens/TitleScreen.tsx
 * DAG selector — lists every unlocked cluster (grouped by world) and every
 * available portal. Letter keys A–Q enter clusters.
 *
 * Phase 4.2 M10k — portals use a [Z] jump-mode (press Z, type the portal
 * number, Enter to jump) so navigation scales past 9 portals; and [U] toggles
 * a session-only hide-cleared filter.
 *
 * Reveal policy (spec §6):
 *   - Locked clusters are not rendered (no DOM node at all)
 *   - Unavailable portals are not rendered
 *   - World headers only render when ≥1 of their clusters is unlocked
 */
import { useMemo, useState } from 'react';
import type { ClusterState, PortalState, MapResp, ProfileResp } from '../../api.js';
// Phase 4.2 M9-patch4 — portal tier labelling on TitleScreen.
// Server emits isPermanent boolean only — we need to distinguish CONDITIONAL
// vs PATH6 client-side. (Path6 portals have portal_clusters=[] on the server
// by DAG Gate-2 design, so they don't fit either hardcoded list and were
// being mislabelled "cond" pre-patch.)
import { CONDITIONAL_PORTALS } from '@econquest/shared';
import { TerminalFrame } from '../components/TerminalFrame.js';
import { KeyBar, type KeyBarItem } from '../components/KeyBar.js';
import { AsciiTitle } from '../components/AsciiTitle.js';
import { useKeyboard, type KeyHandler } from '../hooks/useKeyboard.js';
import { TERMINAL_DEV_MODE } from '../config.js';

interface Props {
  student: { id: string; email: string; name: string };
  map: MapResp;
  profile: ProfileResp | null;
  onEnterCluster: (cluster: ClusterState) => void;
  onEnterPortal: (portal: PortalState) => void;
  onLogout: () => void;
  onQuitToGraph?: () => void;          // dev-mode only
  /** M13 — tier-locked nodes route here (upgrade CTA → Account) instead of entering. */
  onUpgrade?: () => void;
  /** Phase 4.2 M7 — open the account screen. */
  onOpenAccount?: () => void;
  /** Phase 4.2 M9-patch11 — open the read-only Concept Web (/overview) in a new window. */
  onOpenConceptWeb?: () => void;
  /** Phase 4.2 M10 — open the global leaderboard screen. */
  onOpenLeaderboard?: () => void;
}

interface UnlockedListItem {
  cluster: ClusterState;
  status: 'open' | 'in_progress' | 'cleared';
  statusText: string;
}

interface PortalRowInfo {
  /** 'perm' → permanent (server isPermanent=true); 'cond' → conditional
   *  (portalKey in CONDITIONAL_PORTALS); 'path6' → everything else (DAG
   *  path-6 portals; portal_clusters=[] on server, discovered dynamically
   *  via seed_body.portal_key in M9-patch3). */
  label: 'perm' | 'cond' | 'path6';
  /** Cluster-style status, e.g. "0/4 cleared" or "✓ 4/4 cleared" or
   *  "(no content yet)" when total=0. Matches the cluster row format above. */
  progressText: string;
  /** True when solved >= total > 0 — used to apply the same
   *  tm-cluster-row--cleared muted styling as cleared clusters. */
  isFullyCleared: boolean;
}

const CONDITIONAL_PORTALS_SET: ReadonlySet<string> = new Set<string>(CONDITIONAL_PORTALS);

function classifyPortal(p: PortalState): PortalRowInfo {
  // Tier label — mirrors server portalTier() but for display. We can't
  // rely solely on the markValue/tier because PATH6 and CONDITIONAL share
  // tier=6 in some authored content; the auth-source-of-truth is membership.
  let label: PortalRowInfo['label'];
  if (p.isPermanent) {
    label = 'perm';
  } else if (CONDITIONAL_PORTALS_SET.has(p.portalKey)) {
    label = 'cond';
  } else {
    label = 'path6';
  }
  // Progress — cluster-style. Matches the wording the cluster rows use
  // ("0/N open", "✓ N/N cleared") so the UX is consistent.
  const total = p.total ?? 0;
  const solved = p.solved ?? 0;
  const isFullyCleared = total > 0 && solved >= total;
  let progressText: string;
  if (total === 0) {
    progressText = '(no content yet)';
  } else if (isFullyCleared) {
    progressText = `\u2713 ${solved}/${total} cleared`;
  } else {
    progressText = `${solved}/${total} cleared`;
  }
  return { label, progressText, isFullyCleared };
}

function classify(c: ClusterState): UnlockedListItem {
  // Display DISTINCT concept-question counts (matches graph-mode 7/11),
  // NOT raw regularBattlesTotal (which is encounter counter and double-counts retries).
  const total = c.conceptTotal;
  const cleared = c.conceptCleared;
  const isCleared = c.clearedLatched || c.stageCleared;
  if (isCleared) {
    return {
      cluster: c,
      status: 'cleared',
      statusText: total > 0 ? `\u2713 ${cleared}/${total} cleared` : '\u2713 cleared',
    };
  }
  if (total === 0) {
    return { cluster: c, status: 'open', statusText: '(no content yet)' };
  }
  if (cleared === 0) {
    return { cluster: c, status: 'open', statusText: `0/${total} open` };
  }
  const pct = Math.round((cleared / total) * 100);
  return {
    cluster: c,
    status: 'in_progress',
    statusText: `${cleared}/${total}  ${pct}%`,
  };
}

export function TitleScreen({
  student,
  map,
  profile,
  onEnterCluster,
  onEnterPortal,
  onLogout,
  onQuitToGraph,
  onUpgrade,
  onOpenAccount,
  onOpenConceptWeb,
  onOpenLeaderboard,
}: Props) {
  // M13 — tier-locked nodes stay visible (they unlocked via progress) but route
  // to the upgrade CTA instead of a doomed battle entry. Falls back to the normal
  // enter handler when no onUpgrade is wired (e.g. sme/admin never see locks).
  const selectCluster = (c: ClusterState) => {
    if (c.tierLocked && onUpgrade) onUpgrade();
    else onEnterCluster(c);
  };
  const selectPortal = (p: PortalState) => {
    if (p.tierLocked && onUpgrade) onUpgrade();
    else onEnterPortal(p);
  };
  const { world1, world2, portals } = useMemo(() => {
    const unlocked = map.clusters.filter((c) => !c.locked && c.active);
    unlocked.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const w1 = unlocked.filter((c) => c.world === 1).map(classify);
    const w2 = unlocked.filter((c) => c.world === 2).map(classify);
    const p = map.portals.filter((p) => p.available);
    return { world1: w1, world2: w2, portals: p };
  }, [map]);

  // Phase 4.2 M10k — [Z] portal jump-mode: press Z → a PORTAL ▸ {buffer} input
  // line appears; type the portal number, Enter to jump, Esc to cancel,
  // Backspace to edit. Replaces the old bare-digit nav (capped at 9).
  const [jumpMode, setJumpMode] = useState(false);
  const [jumpBuffer, setJumpBuffer] = useState('');
  // Phase 4.2 M10k — [U] hide-cleared: session-only (no localStorage), resets
  // on remount by design.
  const [hideCleared, setHideCleared] = useState(false);

  // Phase 4.2 M10k — hide-filtered cluster lists. When hideCleared is on, cleared
  // clusters drop out of both the rendered lists AND the letter key handlers.
  const { visibleWorld1, visibleWorld2 } = useMemo(() => {
    if (!hideCleared) return { visibleWorld1: world1, visibleWorld2: world2 };
    const keep = (it: UnlockedListItem) => it.status !== 'cleared';
    return { visibleWorld1: world1.filter(keep), visibleWorld2: world2.filter(keep) };
  }, [world1, world2, hideCleared]);

  // Phase 4.2 M10k — visible portals keep STABLE Z-numbering: each entry carries
  // its index into the FULL portals array, so hiding cleared ones leaves the
  // remaining rows on their original [Z{i+1}] labels (e.g. Z1, Z3, Z4…).
  const visiblePortals = useMemo(
    () =>
      portals
        .map((p, i) => ({ p, i }))
        .filter(({ p }) => !(hideCleared && classifyPortal(p).isFullyCleared)),
    [portals, hideCleared],
  );

  // Phase 4.2 M10k — a portal is jumpable when it exists and is not currently
  // hidden by the hide-cleared filter. Shared by the jump-mode commit guard.
  const isPortalJumpable = (oneBasedN: number): boolean => {
    if (!(oneBasedN >= 1 && oneBasedN <= portals.length)) return false;
    const p = portals[oneBasedN - 1];
    if (hideCleared && classifyPortal(p).isFullyCleared) return false;
    return true;
  };

  // Normal handler set: cluster letters, [Z] jump, [U] hide-cleared, and the
  // existing W/Y/V/X/(dev Q) keys. Active only when NOT in jump-mode.
  const normalHandlers: KeyHandler[] = useMemo(() => {
    const hs: KeyHandler[] = [];
    // Phase 4.2 M10k — only non-hidden clusters bind their letter key.
    for (const item of [...visibleWorld1, ...visibleWorld2]) {
      const c = item.cluster;
      hs.push({
        key: c.clusterId,
        handler: () => selectCluster(c),
      });
    }
    // Phase 4.2 M10k — [Z] enters portal jump-mode (only when portals exist).
    if (portals.length > 0) {
      hs.push({ key: 'z', handler: () => { setJumpMode(true); setJumpBuffer(''); } });
    }
    // Phase 4.2 M10k — [U] toggles the session-only hide-cleared filter.
    hs.push({ key: 'u', handler: () => setHideCleared((v) => !v) });
    // Phase 4.1: [R] review key removed; SMEs reach /review via direct URL.
    if (TERMINAL_DEV_MODE) {
      if (onQuitToGraph) hs.push({ key: 'q', handler: onQuitToGraph });
    }
    // Phase 4.1 patch3: [X] logout. Safe because cluster IDs are A–Q, so X never
    // collides with a cluster key.
    hs.push({ key: 'x', handler: onLogout });
    // Phase 4.2 M9-patch11 — [Y] account (moved from [A] which clashed with Cluster A).
    if (onOpenAccount) hs.push({ key: 'y', handler: onOpenAccount });
    // Phase 4.2 M9-patch11 — [W] Concept Web (read-only /overview in a new window).
    if (onOpenConceptWeb) hs.push({ key: 'w', handler: onOpenConceptWeb });
    // Phase 4.2 M10 — [V] global leaderboard. V never collides with cluster IDs (A–Q).
    if (onOpenLeaderboard) hs.push({ key: 'v', handler: onOpenLeaderboard });
    return hs;
  }, [visibleWorld1, visibleWorld2, portals, onEnterCluster, onUpgrade, onQuitToGraph, onLogout, onOpenAccount, onOpenConceptWeb, onOpenLeaderboard]);

  // Phase 4.2 M10k — jump-mode handler set. Active only while jumpMode is true.
  // Digits build the buffer (capped to the digit-width of portals.length), Enter
  // commits, Esc cancels, Backspace edits. All preventDefault so keys don't leak.
  const jumpHandlers: KeyHandler[] = useMemo(() => {
    const maxLen = String(portals.length).length;
    const hs: KeyHandler[] = [];
    for (let d = 0; d <= 9; d++) {
      hs.push({
        key: String(d),
        handler: () => setJumpBuffer((b) => (b.length >= maxLen ? b : b + String(d))),
      });
    }
    hs.push({
      key: 'Enter',
      handler: () => {
        const n = parseInt(jumpBuffer, 10);
        if (Number.isFinite(n) && isPortalJumpable(n)) {
          selectPortal(portals[n - 1]);
          setJumpMode(false);
          setJumpBuffer('');
        } else {
          // Invalid/empty/hidden → clear buffer and stay in jump-mode.
          setJumpBuffer('');
        }
      },
    });
    hs.push({ key: 'Escape', handler: () => { setJumpMode(false); setJumpBuffer(''); } });
    hs.push({ key: 'Backspace', handler: () => setJumpBuffer((b) => b.slice(0, -1)) });
    return hs;
    // isPortalJumpable closes over portals + hideCleared; jumpBuffer is read in Enter.
  }, [portals, jumpBuffer, hideCleared, onEnterPortal, onUpgrade]);

  // Phase 4.2 M10k — two scopes, both hooks called unconditionally; the `enabled`
  // arg toggles which set is live so we never call a hook conditionally.
  useKeyboard(normalHandlers, !jumpMode);
  useKeyboard(jumpHandlers, jumpMode);

  // Build keybar items
  const keybar: KeyBarItem[] = [];
  // Phase 4.2 M10k — keybar reflects the VISIBLE (hide-filtered) cluster letters.
  const clusterLetters = [...visibleWorld1, ...visibleWorld2].map((it) => it.cluster.clusterId).join('/');
  if (clusterLetters) {
    keybar.push({ label: clusterLetters, description: 'enter cluster' });
  }
  // Phase 4.2 M10k — single [Z] jump-to-portal item replaces Z1/Z2/.../Z9.
  if (portals.length > 0) {
    keybar.push({
      label: 'Z',
      description: 'jump to portal',
      variant: 'portal',
      onActivate: () => { setJumpMode(true); setJumpBuffer(''); },
    });
  }
  // Phase 4.2 M10k — [U] toggle hide-cleared.
  keybar.push({
    label: 'U',
    description: hideCleared ? 'show cleared' : 'hide cleared',
    onActivate: () => setHideCleared((v) => !v),
  });
  // Phase 4.1: [R] review keybar item removed.
  if (TERMINAL_DEV_MODE) {
    if (onQuitToGraph) {
      keybar.push({ label: 'Q', description: 'graph mode', onActivate: onQuitToGraph });
    }
  }
  // Phase 4.2 M9-patch11 — [W] Concept Web (read-only /overview opens in a new window).
  if (onOpenConceptWeb) {
    keybar.push({ label: 'W', description: 'concept web', onActivate: onOpenConceptWeb });
  }
  // Phase 4.2 M9-patch11 — [Y] account (moved from [A] to avoid clashing with Cluster A).
  if (onOpenAccount) {
    keybar.push({ label: 'Y', description: 'account', onActivate: onOpenAccount });
  }
  // Phase 4.2 M10 — [V] global leaderboard.
  if (onOpenLeaderboard) {
    keybar.push({ label: 'V', description: 'leaderboard', onActivate: onOpenLeaderboard });
  }
  // Phase 4.1 patch3: [X] logout — tappable label + keyboard binding.
  keybar.push({ label: 'X', description: 'logout', onActivate: onLogout });

  const xp = profile?.gameState.totalXp ?? 0;
  const level = profile?.gameState.level ?? 1;

  // Phase 4.2 M12i — exam countdown widget (top-right meta slot). Days are
  // computed here so the number is always fresh: calendar-day diff between
  // local midnight today and local midnight on the ISO exam date. Hidden when
  // either knob is blank, the date fails to parse, or the date is in the past.
  const countdownNode = useMemo(() => buildCountdownNode(profile?.examCountdown ?? null), [profile?.examCountdown]);

  const titleNode = (
    <>
      ECONQUEST <span className="tm-text--dim">{'\u2500'}</span> HERO {student.name}
      {' '}<span className="tm-text--dim">{'\u2500'}</span>{' '}
      <span className="tm-text--accent">XP {xp.toLocaleString()}</span>
      {' '}<span className="tm-text--dim">{'\u2500'}</span>{' '}
      <span className="tm-text--accent">LVL {level}</span>
    </>
  );

  return (
    <TerminalFrame title={titleNode} meta={countdownNode} keybar={<KeyBar items={keybar} />}>
      <AsciiTitle />
      <div className="tm-section" style={{ textAlign: 'center', color: 'var(--term-dim)' }}>
        Edexcel IGCSE Economics — A Hero's Journey
      </div>

      <div className="tm-section">
        <div className="tm-prompt">{'>'}</div> Choose your next path:
      </div>

      {/* Phase 4.2 M10k — jump-mode input line, shown directly under the prompt. */}
      {jumpMode && (
        <div className="tm-section">
          <span className="tm-text--portal">PORTAL {'▸'} </span>
          <span className="tm-text--accent">{jumpBuffer}</span>
          <span className="tm-text--accent">{'█'}</span>
          {'  '}
          <span className="tm-text--dim">(type number {'·'} Enter to jump {'·'} Esc to cancel)</span>
        </div>
      )}

      {visibleWorld1.length > 0 && (
        <div className="tm-section">
          <div className="tm-h1">WORLD 1 — The Microeconomic Realm</div>
          {visibleWorld1.map((it) => (
            <ClusterListRow key={it.cluster.clusterId} item={it} onEnter={() => selectCluster(it.cluster)} />
          ))}
        </div>
      )}

      {visibleWorld2.length > 0 && (
        <div className="tm-section">
          <div className="tm-h1">WORLD 2 — The Macroeconomic Realm</div>
          {visibleWorld2.map((it) => (
            <ClusterListRow key={it.cluster.clusterId} item={it} onEnter={() => selectCluster(it.cluster)} />
          ))}
        </div>
      )}

      {/* Phase 4.2 M10k — render ALL portals (no slice). When hideCleared is on,
          fully-cleared portals are skipped via visiblePortals, but the surviving
          rows keep their stable [Z{i+1}] label (i = index in the full array). */}
      {portals.length > 0 && visiblePortals.length > 0 && (
        <div className="tm-section">
          <div className="tm-h1 tm-text--portal">{'\u26A1'} PORTALS AVAILABLE</div>
          {visiblePortals.map(({ p, i }) => {
            // M9-patch4: classify portal tier (perm/cond/path6) and compute
            // cluster-style N/M progress. When fully cleared, apply the same
            // muted row styling clusters use so the screen reads consistently.
            const info = classifyPortal(p);
            // M13 \u2014 tier-locked portals stay visible with a \uD83D\uDD12 and route to upgrade.
            const locked = p.tierLocked;
            const rowCls =
              'tm-cluster-row tm-portal-row' +
              (info.isFullyCleared ? ' tm-cluster-row--cleared' : '') +
              (locked ? ' tm-cluster-row--locked' : '');
            return (
              <div key={p.portalKey} className={rowCls}>
                <button
                  type="button"
                  className="tm-cluster-row__hit"
                  onClick={() => selectPortal(p)}
                >
                  <span className="tm-cluster-row__key">[Z{i + 1}]</span>
                  <span className="tm-cluster-row__name">
                    {locked && <span className="tm-lock">{'\u{1F512}'} </span>}
                    <span className="tm-text--portal">{p.endpointA} {'\u2194'} {p.endpointB}</span>
                  </span>
                  <span className="tm-cluster-row__status">
                    {locked
                      ? 'upgrade to unlock'
                      : <>{info.label} {'\u00B7'} {p.tier}-mark {'\u00B7'} pass {'\u2265'} {p.passBar} {'\u00B7'} {info.progressText}</>}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {world1.length === 0 && world2.length === 0 && (
        <div className="tm-empty">No clusters unlocked yet. Press [X] to log out and switch account.</div>

      )}

      {/* Phase 4.2 M10k — distinct hint when everything visible was filtered out
          by hide-cleared (the map itself is NOT empty). */}
      {!(world1.length === 0 && world2.length === 0) &&
        visibleWorld1.length === 0 &&
        visibleWorld2.length === 0 &&
        visiblePortals.length === 0 && (
          <div className="tm-empty">All cleared paths hidden — press [U] to show.</div>
        )}
    </TerminalFrame>
  );
}

/**
 * Phase 4.2 M12i — build the two-line exam countdown node for the dashboard
 * top-right. Returns undefined (renders nothing) when the widget should hide:
 * blank name/date, an unparseable date, or a date already in the past.
 */
function buildCountdownNode(cd: { name: string; date: string } | null) {
  if (!cd) return undefined;
  const name = cd.name.trim();
  const date = cd.date.trim();
  if (!name || !date) return undefined;

  const exam = new Date(date + 'T00:00:00');
  if (Number.isNaN(exam.getTime())) return undefined;

  const now = new Date();
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const examMidnight = new Date(exam.getFullYear(), exam.getMonth(), exam.getDate()).getTime();
  const days = Math.round((examMidnight - todayMidnight) / 86_400_000);
  if (days < 0) return undefined;

  const daysText = days === 0 ? 'exam today' : days === 1 ? '1 day to exam' : `${days} days to exam`;

  return (
    <div className="tm-countdown">
      <div className="tm-countdown__name">{name}</div>
      <div className="tm-countdown__days">{daysText}</div>
    </div>
  );
}

function ClusterListRow({ item, onEnter }: { item: UnlockedListItem; onEnter: () => void }) {
  // M13 — tier-locked clusters stay visible but show a 🔒 and route to upgrade.
  const locked = item.cluster.tierLocked;
  const cls =
    'tm-cluster-row' +
    (item.status === 'cleared' ? ' tm-cluster-row--cleared' : '') +
    (locked ? ' tm-cluster-row--locked' : '');
  return (
    <div className={cls}>
      <button type="button" className="tm-cluster-row__hit" onClick={onEnter}>
        <span className="tm-cluster-row__key">[{item.cluster.clusterId}]</span>
        <span className="tm-cluster-row__name">
          {locked && <span className="tm-lock">{'\u{1F512}'} </span>}
          {item.cluster.fullName}
        </span>
        <span className="tm-cluster-row__status">{locked ? 'upgrade to unlock' : item.statusText}</span>
      </button>
    </div>
  );
}
